import React from 'react';
import {
  DataTable,
  type DataTableStateEvent,
  type DataTableRowClickEvent,
} from 'primereact/datatable';

import { Column } from 'primereact/column';
import DateUtils from '../../../utils/DateTimeUtils';
import { type ITrip } from '../../../models/Trip';
import dayjs from 'dayjs';
import useTenantStore, { selectTenantId } from '../../../stores/TenantStore';
import { formatMileage, noop } from '../../../utils/GeneralUtils';
import OpenAPI from '../../../endpoint/Openapi';
import { type paths } from '../../../endpoint/Schema';
import { useNavigate } from 'react-router-dom';

const ROWS = 20;

interface ITripProps {
  userId: string;
}

function UserTrips({ userId }: ITripProps) {
  const tenantId = useTenantStore(selectTenantId);
  const navigate = useNavigate();
  const [lazyParams, setLazyParams] = React.useState<
    Pick<DataTableStateEvent, 'first' | 'rows' | 'page'>
  >({
    first: 0,
    rows: ROWS,
    page: 1,
  });
  const [loading, setLoading] = React.useState(false);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/trips/{userId}']['get']['responses']['200']['content']['application/json']
    >();

  React.useEffect(() => {
    const getTrips = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/trips/{userId}', {
          params: {
            path: { userId },
            query: {
              tenantId,
              skip: lazyParams.first,
              take: lazyParams.rows,
            },
          },
        });

        setData(res.data);
      } catch {}

      setLoading(false);
    };

    getTrips().catch(noop);
  }, [lazyParams.first, lazyParams.rows, tenantId, userId]);

  function onPage(event: DataTableStateEvent) {
    setLazyParams((prev) => ({ ...prev, ...event }));
  }
  function startDateBodyTemplate(rowData: ITrip) {
    return DateUtils.formatDateTime(rowData.startDate);
  }

  function endDateBodyTemplate(rowData: ITrip) {
    return DateUtils.formatDateTime(rowData.endDate);
  }

  function durationBodyTemplate(rowData: ITrip) {
    return dayjs.duration(rowData.duration, 'minutes').format('HH:mm:ss');
  }

  function scoreBodyTemplate(rowData: ITrip) {
    return rowData.score ? Math.round(rowData.score) : '-';
  }

  function accelerationScoreBodyTemplate(rowData: ITrip) {
    return rowData.accelerationScore
      ? Math.round(rowData.accelerationScore)
      : '-';
  }

  function brakingScoreBodyTemplate(rowData: ITrip) {
    return rowData.brakingScore ? Math.round(rowData.brakingScore) : '-';
  }

  function corneringScoreBodyTemplate(rowData: ITrip) {
    return rowData.corneringScore ? Math.round(rowData.corneringScore) : '-';
  }

  function speedingScoreBodyTemplate(rowData: ITrip) {
    return rowData.speedingScore ? Math.round(rowData.speedingScore) : '-';
  }

  function phoneUsageScoreBodyTemplate(rowData: ITrip) {
    return rowData.phoneUsageScore ? Math.round(rowData.phoneUsageScore) : '-';
  }

  function mileageBodyTemplate(rowData: ITrip) {
    return formatMileage(rowData.mileage);
  }

  function userBodyTemplate(rowData: ITrip) {
    return rowData.userName ?? rowData.userEmail ?? 'N/A';
  }

  function onRowClick(event: DataTableRowClickEvent) {
    const tripData = event.data as ITrip;
    const tripId = tripData.id;
    navigate(`/user/${userId}/trip/${tripId}`);
  }
  return (
    <div style={{ maxWidth: window.innerWidth * 0.8 }}>
      <DataTable
        value={data?.page ?? []}
        dataKey="id"
        scrollable
        paginator
        rows={ROWS}
        loading={loading}
        first={lazyParams.first}
        totalRecords={data?.count ?? 0}
        rowHover
        lazy
        onPage={onPage}
        filterDisplay="row"
        selectionMode="single"
        onRowClick={onRowClick}
        style={{ cursor: 'pointer' }}
      >
        <Column field="userName" header="User" body={userBodyTemplate} />
        <Column field="transportType" header="Role" />
        <Column
          field="duration"
          header="Duration"
          body={durationBodyTemplate}
        />
        <Column field="mileage" header="Mileage" body={mileageBodyTemplate} />
        <Column field="startPoint" header="Start Point" />
        <Column field="endPoint" header="End Point" />
        <Column
          field="startDate"
          header="Start Time"
          body={startDateBodyTemplate}
        />
        <Column field="endDate" header="End Time" body={endDateBodyTemplate} />
        <Column field="score" header="Safety Score" body={scoreBodyTemplate} />
        <Column
          field="accelerationScore"
          header="Acceleration"
          body={accelerationScoreBodyTemplate}
        />
        <Column
          field="brakingScore"
          header="Braking"
          body={brakingScoreBodyTemplate}
        />
        <Column
          field="corneringScore"
          header="Cornering"
          body={corneringScoreBodyTemplate}
        />
        <Column
          field="speedingScore"
          header="Speeding"
          body={speedingScoreBodyTemplate}
        />
        <Column
          field="phoneUsageScore"
          header="Phone Usage"
          body={phoneUsageScoreBodyTemplate}
        />
      </DataTable>
    </div>
  );
}

export default UserTrips;
